
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { BoListPanel } from 'bonomo/dist';
import CommunityNav from '@/components/community/widget/CommunityNav.vue';
import Datim from '@/components/shared/Datim.vue';
import LockerChip from '@/components/shared/chips/LockerChip.vue';
import UserChip from '@/components/shared/chips/UserChip.vue';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import { ILocker } from '@/model/Locker/lockerInterfaces';
import { ICommunity } from '@/model/Community/communityInterfaces';
import { communityUserService, ICommunityUser } from '@/services/communityUserService';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';
import { extractApiErrorMessage } from '@/helpers/errorsHelper';
import { downloadFileByUrl } from '@/helpers/downloadHelper';
import { PAGINATION_FIRST_PAGE, PAGINATION_DEFAULT_PER_PAGE, PAGINATION_PER_PAGE_OPTIONS } from '@/constants/pagination';

const lockerModule = namespace('lockerModule');
const communityModule = namespace('communityModule');

@Component({
  name: 'CommunityUsers',
  components: { BoListPanel, CommunityNav, Datim, LockerChip, UserChip, PaginationBar },
})
export default class CommunityUsers extends Vue {
  @Prop()
  protected communityId!: number;

  @lockerModule.Getter('lockers')
  private lockers!: ILocker[];

  @communityModule.Getter('community')
  protected community!: ICommunity | null;

  @communityModule.Action('fetchCommunity')
  protected fetchCommunity!: (communityId: number) => Promise<any>;

  protected loading: boolean = true;
  protected downloadingCsv: boolean = false;
  protected users: ICommunityUser[] = [];

  protected currentPage: number = PAGINATION_FIRST_PAGE;
  protected perPage: number = PAGINATION_DEFAULT_PER_PAGE;
  protected lastPage: number = PAGINATION_FIRST_PAGE;
  protected total: number = 0;
  protected readonly perPageOptions: number[] = PAGINATION_PER_PAGE_OPTIONS;

  protected get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('common.id'), align: 'start', sortable: false, value: 'id' },
      { text: this.$t('users.userList.email'), align: 'start', sortable: false, value: 'email' },
      { text: this.$t('users.userList.firstName'), align: 'start', sortable: false, value: 'first_name' },
      { text: this.$t('users.userList.lastName'), align: 'start', sortable: false, value: 'last_name' },
      { text: this.$t('users.userList.phone'), align: 'start', sortable: false, value: 'phone_number' },
      { text: this.$t('lockers.locker.title'), align: 'start', sortable: false, value: 'locker_id' },
      { text: this.$t('users.userList.createdAt'), align: 'start', sortable: false, value: 'created_at' },
      { text: '', value: 'detailButton' },
    ];
  }

  async loadData() {
    this.loading = true;
    try {
      if (!this.lockers || !this.lockers.length) {
        this.$store.dispatch('lockerModule/fetchLockers');
      }
      this.fetchCommunity(this.communityId);
      const page = await communityUserService.fetchByCommunity(this.communityId, {
        page: this.currentPage,
        per_page: this.perPage,
      });
      this.users = page.data;
      this.currentPage = page.current_page;
      this.lastPage = page.last_page;
      this.total = page.total;
    } catch (e) {
      this.$uiBus.emit('alert', extractApiErrorMessage(e));
    } finally {
      this.loading = false;
    }
  }

  getLockerName(lockerId: number): string {
    for (let i = 0; i < this.lockers.length; i++) {
      if (this.lockers[i].id == lockerId) {
        return this.lockers[i].name;
      }
    }
    return '---';
  }

  toUserDetail(userId: number): any {
    return { name: 'user', params: { userId: `${userId}` } };
  }

  protected onPageChange(page: number) {
    this.currentPage = page;
    this.loadData();
  }

  protected onPerPageChange(value: number) {
    this.perPage = value;
    this.currentPage = PAGINATION_FIRST_PAGE;
    this.loadData();
  }

  protected async downloadUsersCsv() {
    this.downloadingCsv = true;
    try {
      const page = await communityUserService.fetchByCommunity(this.communityId, {
        page: 1,
        per_page: this.total || this.perPage,
      });
      const headers = ['id', 'email', 'name', 'last_name', 'register_date', 'status'];
      const rows = page.data.map((user: ICommunityUser) => [
        user.id,
        user.email || '',
        user.first_name || '',
        user.last_name || '',
        user.created_at || '',
        user.active ? 'active' : 'inactive',
      ]);
      const csv = [headers, ...rows]
        .map((line: Array<string | number>) => line.map(this.escapeCsvCell).join(','))
        .join('\n');
      const csvBlob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(csvBlob);
      downloadFileByUrl(url, `community-${this.communityId}-users.csv`);
      URL.revokeObjectURL(url);
    } catch (e) {
      this.$uiBus.emit('alert', extractApiErrorMessage(e));
    } finally {
      this.downloadingCsv = false;
    }
  }

  private escapeCsvCell(value: string | number): string {
    const safeValue = value === null || value === undefined ? '' : `${value}`;
    return `"${safeValue.replace(/"/g, '""')}"`;
  }
}
